import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Lock } from "lucide-react";
import { api, errorMessage, post, put } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { loginFieldLocked } from "@/utils/loginContactLock";

type ChildRow = { studentUserId: string; studentName: string };

type GuardianLink = {
  relationshipId?: string | null;
  userId: string | null;
  kind: string;
  name: string;
  label: string;
  email?: string | null;
  primary?: boolean;
  emergency?: boolean;
  guardianType?: string | null;
  studentUserId: string;
  studentName: string;
};

const GUARDIAN_TYPE_OPTIONS = [
  { value: "father", label: "Father" },
  { value: "mother", label: "Mother" },
  { value: "grandfather", label: "Grandfather" },
  { value: "grandmother", label: "Grandmother" },
  { value: "step_father", label: "Step Father" },
  { value: "step_mother", label: "Step Mother" },
  { value: "foster_parent", label: "Foster Parent" },
  { value: "other", label: "Other" },
] as const;

type RelationBadge = {
  key: string;
  text: string;
  emergency?: boolean;
};

type ContactTarget = {
  id: string;
  kind: "self" | "student" | "guardian";
  name: string;
  detail: string;
  email: string;
  editable: boolean;
  emailLocked: boolean;
  routeStudentId: string;
  badges: RelationBadge[];
};

const FIELDS = [
  { id: "email", label: "Email", type: "email", autoComplete: "email", wide: true },
  { id: "phone", label: "Phone", type: "tel", autoComplete: "tel", wide: true },
  { id: "street", label: "Street address", type: "text", autoComplete: "street-address", wide: true },
  { id: "city", label: "City", type: "text", autoComplete: "address-level2", wide: false },
  { id: "state", label: "State", type: "text", autoComplete: "address-level1", wide: false },
  { id: "zip", label: "ZIP code", type: "text", autoComplete: "postal-code", wide: false },
] as const;

type FieldId = (typeof FIELDS)[number]["id"];

type ContactRecord = {
  userId: string;
  email: string;
  phone: string;
  canLogin: boolean;
  emailEditable?: boolean;
  phoneEditable?: boolean;
  street: string;
  city: string;
  state: string;
  zip: string;
};

function valuesFrom(row: ContactRecord): Record<FieldId, string> {
  return {
    email: row.email || "",
    phone: row.phone || "",
    street: row.street || "",
    city: row.city || "",
    state: row.state || "",
    zip: row.zip || "",
  };
}

function personId(cardId: string) {
  const split = cardId.indexOf(":");
  return split >= 0 ? cardId.slice(split + 1) : "";
}

const EMPTY_VALUES: Record<FieldId, string> = {
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zip: "",
};

type GuardianHit = { id: string; email?: string | null; first_name?: string | null; last_name?: string | null };

function AddGuardianForm({
  studentUserId,
  hasPrimary,
  onAdded,
}: {
  studentUserId: string;
  hasPrimary: boolean;
  onAdded: (link: GuardianLink) => void;
}) {
  const { capabilities } = useAuth();
  const canSearch = Boolean(capabilities?.searchPeople);
  const [mode, setMode] = useState<"search" | "create">("create");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<GuardianHit[]>([]);
  const [searched, setSearched] = useState(false);
  const [picked, setPicked] = useState<GuardianHit | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [guardianType, setGuardianType] = useState("mother");
  const [primary, setPrimary] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const ready = mode === "create" ? Boolean(firstName.trim() && lastName.trim()) : Boolean(picked);

  return (
    <form
      className="parent-relationship"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready || busy) return;
        setBusy(true);
        setError("");
        const body =
          mode === "create"
            ? { mode: "create", firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), guardianType, primary, emergency }
            : { mode: "search", userId: picked?.id, guardianType, primary, emergency };
        post<{ success: boolean; data: GuardianLink }>(
          `/api/parent/students/${encodeURIComponent(studentUserId)}/person-links`,
          body,
        )
          .then((env) => {
            if (!env.data) return;
            onAdded(env.data);
            setQuery("");
            setHits([]);
            setSearched(false);
            setPicked(null);
            setFirstName("");
            setLastName("");
            setEmail("");
            setPrimary(false);
            setEmergency(false);
          })
          .catch((err) => setError(errorMessage(err)))
          .finally(() => setBusy(false));
      }}
    >
      <h2 id="parent-add-guardian-heading" className="parent-contact-panel__title">Add a guardian</h2>
      <p className="parent-contact-panel__lede">Create a new guardian for this student.</p>
      {canSearch ? (
        <div className="parent-relationship__modes" role="group" aria-label="How to add a guardian">
          <button type="button" className={mode === "search" ? "is-on" : ""} disabled={busy} onClick={() => setMode("search")}>
            Search existing
          </button>
          <button type="button" className={mode === "create" ? "is-on" : ""} disabled={busy} onClick={() => setMode("create")}>
            Create new
          </button>
        </div>
      ) : null}
      {canSearch && mode === "search" ? (
        <>
          <div className="parent-relationship__search">
            <input
              className="student-input"
              value={query}
              placeholder="Name or email"
              aria-label="Find a guardian"
              disabled={busy}
              onChange={(event) => setQuery(event.target.value)}
            />
            <button
              type="button"
              className="student-btn student-btn--ghost"
              disabled={busy || !query.trim()}
              onClick={() => {
                setBusy(true);
                setError("");
                setPicked(null);
                api<{ success: boolean; data: GuardianHit[] }>(
                  `/api/parent/students/${encodeURIComponent(studentUserId)}/guardian-search?q=${encodeURIComponent(query.trim())}`,
                )
                  .then((env) => {
                    setHits(env.data || []);
                    setSearched(true);
                  })
                  .catch((err) => setError(errorMessage(err)))
                  .finally(() => setBusy(false));
              }}
            >
              Search
            </button>
          </div>
          {searched && !hits.length ? <p className="parent-form-soon">No matches.</p> : null}
          {hits.length ? (
            <div className="parent-relationship__hits">
              {hits.map((hit) => {
                const name = [hit.first_name, hit.last_name].filter(Boolean).join(" ").trim() || hit.email || "Unnamed";
                return (
                  <button
                    key={hit.id}
                    type="button"
                    className={picked?.id === hit.id ? "is-on" : ""}
                    disabled={busy}
                    onClick={() => setPicked(hit)}
                  >
                    {name}
                    {hit.email ? <span>{hit.email}</span> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </>
      ) : (
        <div className="parent-relationship__create">
          <input className="student-input" value={firstName} placeholder="First name" aria-label="First name" disabled={busy} onChange={(event) => setFirstName(event.target.value)} />
          <input className="student-input" value={lastName} placeholder="Last name" aria-label="Last name" disabled={busy} onChange={(event) => setLastName(event.target.value)} />
          <input className="student-input" type="email" value={email} placeholder="Email" aria-label="Email" disabled={busy} onChange={(event) => setEmail(event.target.value)} />
        </div>
      )}
      <label className="parent-field" htmlFor={`parent-add-rel-${studentUserId}`}>
        <span>Relationship</span>
        <select id={`parent-add-rel-${studentUserId}`} className="student-input" value={guardianType} disabled={busy} onChange={(event) => setGuardianType(event.target.value)}>
          {GUARDIAN_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <div className="parent-relationship__checks">
        <label className="parent-relationship__check">
          <input type="checkbox" checked={primary} disabled={busy || hasPrimary} onChange={(event) => setPrimary(event.target.checked)} />
          Primary{hasPrimary ? " already set" : ""}
        </label>
        <label className="parent-relationship__check">
          <input type="checkbox" checked={emergency} disabled={busy} onChange={(event) => setEmergency(event.target.checked)} />
          Emergency
        </label>
      </div>
      {error ? (
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="parent-form-actions">
        <button type="submit" className="student-btn" disabled={busy || !ready}>
          {busy ? "Linking" : "Link"}
        </button>
      </div>
    </form>
  );
}

function RelationshipEditor({
  link,
  otherPrimary,
  onSaved,
}: {
  link: GuardianLink;
  otherPrimary: boolean;
  onSaved: (next: GuardianLink) => void;
}) {
  const savedType = link.guardianType || "other";
  const [guardianType, setGuardianType] = useState(savedType);
  const [primary, setPrimary] = useState(Boolean(link.primary));
  const [emergency, setEmergency] = useState(Boolean(link.emergency));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setGuardianType(link.guardianType || "other");
    setPrimary(Boolean(link.primary));
    setEmergency(Boolean(link.emergency));
    setError("");
  }, [link.emergency, link.guardianType, link.primary, link.relationshipId]);

  const dirty =
    guardianType !== savedType || primary !== Boolean(link.primary) || emergency !== Boolean(link.emergency);
  if (!link.relationshipId) return null;

  return (
    <form
      className="parent-relationship"
      onSubmit={(event) => {
        event.preventDefault();
        if (!dirty || busy || !link.relationshipId) return;
        setBusy(true);
        setError("");
        put<{ success: boolean; data: GuardianLink }>(
          `/api/parent/students/${encodeURIComponent(link.studentUserId)}/person-links/${encodeURIComponent(link.relationshipId)}`,
          { guardianType, primary, emergency },
        )
          .then((env) => {
            if (env.data) onSaved({ ...link, ...env.data, studentUserId: link.studentUserId, studentName: link.studentName });
          })
          .catch((err) => setError(errorMessage(err)))
          .finally(() => setBusy(false));
      }}
    >
      <label className="parent-field" htmlFor={`parent-rel-${link.relationshipId}`}>
        <span>{link.studentName ? `Relationship with ${link.studentName}` : "Relationship"}</span>
        <select
          id={`parent-rel-${link.relationshipId}`}
          className="student-input"
          value={guardianType}
          disabled={busy}
          onChange={(event) => setGuardianType(event.target.value)}
        >
          {GUARDIAN_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <div className="parent-relationship__checks">
        <label className="parent-relationship__check">
          <input
            type="checkbox"
            checked={primary}
            disabled={busy || (otherPrimary && !link.primary)}
            onChange={(event) => setPrimary(event.target.checked)}
          />
          Primary{otherPrimary && !link.primary ? " already set" : ""}
        </label>
        <label className="parent-relationship__check">
          <input
            type="checkbox"
            checked={emergency}
            disabled={busy}
            onChange={(event) => setEmergency(event.target.checked)}
          />
          Emergency
        </label>
      </div>
      {error ? (
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      ) : null}
      {dirty ? (
        <div className="parent-form-actions">
          <button type="submit" className="student-btn" disabled={busy}>
            {busy ? "Saving" : "Save"}
          </button>
        </div>
      ) : null}
    </form>
  );
}

function relationBadges(link: GuardianLink): RelationBadge[] {
  const who = link.studentName || "Student";
  const role = [link.primary ? "Primary" : "", link.label || "Guardian"].filter(Boolean).join(" ");
  const badges: RelationBadge[] = [{ key: `${link.studentUserId}:role`, text: `${role} · ${who}` }];
  if (link.emergency) badges.push({ key: `${link.studentUserId}:emergency`, text: `Emergency · ${who}`, emergency: true });
  return badges;
}

export default function ParentStudentContact({ focusStudent = false }: { focusStudent?: boolean }) {
  const { studentUserId = "" } = useParams();
  const { capabilities, user } = useAuth();
  const canUpdateSelf = Boolean(capabilities?.updateOwnContact);
  const canSeeStudent = Boolean(
    capabilities?.viewStudentContact ||
      capabilities?.updateStudentContact ||
      capabilities?.viewContact ||
      capabilities?.updateContact,
  );
  const canUpdateStudent = Boolean(capabilities?.updateStudentContact || capabilities?.updateContact);
  const canSeeGuardians = Boolean(capabilities?.viewGuardianContact || capabilities?.updateGuardianContact);
  const canUpdateGuardians = Boolean(capabilities?.updateGuardianContact);
  const canManageGuardians = Boolean(capabilities?.manageStudentGuardianRelationships);
  const canLoadLinks = Boolean(
    capabilities?.viewPersonLinks || capabilities?.personLinks || canSeeGuardians || canManageGuardians,
  );
  const [student, setStudent] = useState<ChildRow | null>(null);
  const [children, setChildren] = useState<ChildRow[]>([]);
  const [guardians, setGuardians] = useState<GuardianLink[]>([]);
  const [error, setError] = useState("");
  const [guardianError, setGuardianError] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [values, setValues] = useState<Record<FieldId, string>>(EMPTY_VALUES);
  const [contact, setContact] = useState<ContactRecord | null>(null);
  const [contactError, setContactError] = useState("");
  const [loadingContact, setLoadingContact] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancel = false;
    setStudent(null);
    setChildren([]);
    setGuardians([]);
    setGuardianError("");
    setError("");
    setReady(false);
    api<{ success: boolean; data: ChildRow[] }>("/api/parent/students")
      .then(async (env) => {
        if (cancel) return;
        const rows = env.data || [];
        if (focusStudent) {
          const found = rows.find((row) => row.studentUserId === studentUserId);
          if (found) setStudent(found);
          else setError("That student is not linked to you.");
          setChildren(found ? [found] : []);
          if (!found || !canLoadLinks) return;
          try {
            const links = await api<{ success: boolean; data: Omit<GuardianLink, "studentUserId" | "studentName">[] }>(
              `/api/parent/students/${encodeURIComponent(found.studentUserId)}/person-links`,
            );
            if (!cancel) {
              setGuardians(
                (links.data || []).map((link) => ({
                  ...link,
                  studentUserId: found.studentUserId,
                  studentName: found.studentName,
                })),
              );
            }
          } catch (err) {
            if (!cancel) setGuardianError(errorMessage(err));
          }
          return;
        }
        setChildren(rows);
        if (!canLoadLinks || !rows.length) return;
        const batches = await Promise.all(
          rows.map(async (child) => {
            try {
              const links = await api<{ success: boolean; data: Omit<GuardianLink, "studentUserId" | "studentName">[] }>(
                `/api/parent/students/${encodeURIComponent(child.studentUserId)}/person-links`,
              );
              return (links.data || []).map((link) => ({
                ...link,
                studentUserId: child.studentUserId,
                studentName: child.studentName,
              }));
            } catch (err) {
              if (!cancel) setGuardianError(errorMessage(err));
              return [];
            }
          }),
        );
        if (!cancel) setGuardians(batches.flat());
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancel) setReady(true);
      });
    return () => {
      cancel = true;
    };
  }, [canLoadLinks, focusStudent, studentUserId]);

  const selfId = user?.id != null ? String(user.id) : "";
  const selfName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() || "You";

  const targets = useMemo(() => {
    const roster = focusStudent ? (student ? [student] : []) : children;
    const fallbackStudentId = roster[0]?.studentUserId || "";
    const rows: ContactTarget[] = [];
    if (!focusStudent && canUpdateSelf && selfId) {
      const email = String(user?.email || "").trim();
      const mine = guardians.filter((link) => String(link.userId || "") === selfId);
      rows.push({
        id: `self:${selfId}`,
        kind: "self",
        name: selfName,
        detail: "You",
        email,
        editable: true,
        emailLocked: Boolean(email),
        routeStudentId: mine[0]?.studentUserId || fallbackStudentId,
        badges: [{ key: "self", text: "You" }, ...mine.flatMap(relationBadges)],
      });
    }
    if (canSeeStudent) {
      for (const child of roster) {
        rows.push({
          id: `student:${child.studentUserId}`,
          kind: "student",
          name: child.studentName,
          detail: "Student",
          email: "",
          editable: canUpdateStudent,
          emailLocked: false,
          routeStudentId: child.studentUserId,
          badges: [{ key: `${child.studentUserId}:student`, text: "Student" }],
        });
      }
    }
    if (canSeeGuardians && !focusStudent) {
      const byPerson = new Map<string, ContactTarget>();
      for (const link of guardians) {
        if (String(link.kind || "").toLowerCase() !== "guardian") continue;
        const id = link.userId ? String(link.userId) : "";
        if (!id || id === selfId) continue;
        const existing = byPerson.get(id);
        const badges = relationBadges(link);
        if (existing) {
          existing.badges.push(...badges);
          if (!existing.email && link.email) existing.email = String(link.email).trim();
          continue;
        }
        byPerson.set(id, {
          id: `guardian:${id}`,
          kind: "guardian",
          name: link.name || "Guardian",
          detail: link.label || "Guardian",
          email: String(link.email || "").trim(),
          editable: canUpdateGuardians,
          emailLocked: false,
          routeStudentId: link.studentUserId,
          badges,
        });
      }
      rows.push(...byPerson.values());
    }
    return rows;
  }, [
    canSeeGuardians,
    canSeeStudent,
    canUpdateGuardians,
    canUpdateSelf,
    canUpdateStudent,
    children,
    focusStudent,
    guardians,
    selfId,
    selfName,
    student,
    user?.email,
  ]);

  const selected = focusStudent
    ? targets.find((row) => row.kind === "student") || null
    : targets.find((row) => row.id === selectedId) || null;

  const relationshipLinks = useMemo(() => {
    if (!canManageGuardians || !selected || !selfId) return [];
    const ownerId = selected.kind === "student" ? selfId : personId(selected.id);
    if (!ownerId) return [];
    return guardians.filter(
      (link) =>
        String(link.kind || "").toLowerCase() === "guardian" &&
        String(link.userId || "") === ownerId &&
        Boolean(link.relationshipId) &&
        (!focusStudent || link.studentUserId === selected.routeStudentId),
    );
  }, [canManageGuardians, focusStudent, guardians, selected, selfId]);

  useEffect(() => {
    const row = selected;
    const targetUserId = row ? personId(row.id) : "";
    const own = row?.kind === "self";
    if (!row || (!own && (!targetUserId || !row.routeStudentId))) {
      setContact(null);
      setValues(EMPTY_VALUES);
      return undefined;
    }
    let cancel = false;
    setContact(null);
    setContactError("");
    setSaveError("");
    setSaved("");
    setLoadingContact(true);
    setValues({ ...EMPTY_VALUES, email: row.email });
    api<{ success: boolean; data: ContactRecord }>(
      own
        ? "/api/profile/contact"
        : `/api/parent/students/${encodeURIComponent(row.routeStudentId)}/contacts/${encodeURIComponent(targetUserId)}`,
    )
      .then((env) => {
        if (cancel || !env.data) return;
        setContact(env.data);
        setValues(valuesFrom(env.data));
      })
      .catch((err) => {
        if (!cancel) setContactError(errorMessage(err));
      })
      .finally(() => {
        if (!cancel) setLoadingContact(false);
      });
    return () => {
      cancel = true;
    };
  }, [selected?.id, selected?.kind, selected?.routeStudentId, selected?.email]);

  const back = focusStudent ? (
    <Link className="student-text-link" to={`/parent/student/${encodeURIComponent(studentUserId)}`}>
      <ChevronLeft size={15} /> {student?.studentName || "Back"}
    </Link>
  ) : null;

  const baseline = contact ? valuesFrom(contact) : null;
  const dirty = Boolean(
    selected?.editable &&
      baseline &&
      FIELDS.some((field) => values[field.id].trim() !== baseline[field.id].trim()),
  );

  if (!ready && !error) {
    return (
      <div className="student-page student-page--parent parent-contact-page" aria-busy="true" aria-live="polite">
        <span className="student-vh">Loading contact information</span>
        <div className="student-ghost-head">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--title" />
          <div className="student-ghost student-ghost--line student-ghost--w60" />
        </div>
        <div className="student-ghost-card">
          <div className="student-ghost student-ghost--line student-ghost--w80" />
          <div className="student-ghost student-ghost--choice" />
          <div className="student-ghost student-ghost--line student-ghost--w70" />
          <div className="student-ghost student-ghost--choice" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-page student-page--parent parent-contact-page">
        {back}
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="student-page student-page--parent parent-contact-page">
      {back}
      <div className="student-page-head" style={{ marginTop: focusStudent ? 10 : 0 }}>
        {focusStudent && student ? <span className="student-eyebrow">{student.studentName}</span> : null}
        <h1>Contact information</h1>
        <p>
          {focusStudent
            ? `How the school can reach ${student?.studentName || "this student"}.`
            : "Everyone you can reach. A badge shows the relationship for each student."}
        </p>
        {focusStudent ? (
          <p>
            <Link className="student-text-link" to="/parent/contact">
              All contacts
            </Link>
          </p>
        ) : null}
      </div>
      {guardianError ? (
        <div className="student-inline-error" role="alert">
          {guardianError}
        </div>
      ) : null}
      {focusStudent && !selected ? (
        <p className="parent-form-soon">You do not have permission to see this student&apos;s contact.</p>
      ) : null}
      {!focusStudent && targets.length === 0 ? (
        <p className="parent-form-soon">You do not have permission to see contact information.</p>
      ) : null}
      {!focusStudent && targets.length > 0 ? (
        <ul className="parent-contact-cards">
          {targets.map((target) => {
            const open = target.id === selected?.id;
            return (
              <li key={target.id}>
                <button
                  type="button"
                  className={`parent-contact-card${open ? " is-selected" : ""}`}
                  aria-expanded={open}
                  onClick={() => setSelectedId(open ? "" : target.id)}
                >
                  <span className="parent-contact-card__name">{target.name}</span>
                  <span className="parent-contact-badges">
                    {target.badges.map((badge) => (
                      <span
                        key={badge.key}
                        className={`parent-contact-badge${badge.emergency ? " is-emergency" : ""}`}
                      >
                        {badge.text}
                      </span>
                    ))}
                    {target.editable ? null : <span className="parent-contact-badge">View only</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {selected ? (
        <div className="parent-contact-stack">
          <section className="student-card parent-contact-panel" aria-labelledby="parent-contact-heading">
            <h2 id="parent-contact-heading" className="parent-contact-panel__title">
              {selected.name}
            </h2>
            <p className="parent-contact-panel__lede">Email, phone, and address.</p>
            {contactError ? (
              <div className="student-inline-error" role="alert">
                {contactError}
              </div>
            ) : null}
            <form
              className="parent-form parent-form--grid"
              onSubmit={(event) => {
                event.preventDefault();
                if (!selected.editable || !contact || saving || !dirty) return;
                setSaving(true);
                setSaveError("");
                setSaved("");
              put<{ success: boolean; data: ContactRecord }>(
                selected.kind === "self"
                  ? "/api/profile/contact"
                  : `/api/parent/students/${encodeURIComponent(selected.routeStudentId)}/contacts/${encodeURIComponent(contact.userId)}`,
                values,
              )
                  .then((env) => {
                    if (!env.data) return;
                    setContact(env.data);
                    setValues(valuesFrom(env.data));
                    setSaved("Saved.");
                  })
                  .catch((err) => setSaveError(errorMessage(err)))
                  .finally(() => setSaving(false));
              }}
            >
              {loadingContact ? (
                <div className="parent-field parent-field--wide" aria-hidden="true">
                  <div className="student-ghost student-ghost--line student-ghost--w60" />
                  <div className="student-ghost student-ghost--choice" />
                  <div className="student-ghost student-ghost--line student-ghost--w80" style={{ marginTop: 8 }} />
                  <div className="student-ghost student-ghost--choice" />
                </div>
              ) : (
                FIELDS.map((field) => {
                  const signInLocked =
                    (field.id === "email" || field.id === "phone") &&
                    loginFieldLocked(contact, field.id, capabilities);
                const locked = !selected.editable || signInLocked;
                const half = field.id === "email" || field.id === "phone";
                return (
                  <label
                    key={field.id}
                    className={`parent-field${field.wide && !half ? " parent-field--wide" : ""}${half ? " parent-field--half" : ""}`}
                      htmlFor={`parent-contact-${field.id}`}
                    >
                      <span className="parent-field__label">
                        {field.label}
                        {signInLocked ? (
                          <span className="parent-lock" tabIndex={0}>
                            <Lock size={13} aria-hidden />
                            <span className="student-vh">Locked</span>
                            <span className="parent-lock__tip" role="tooltip">
                              Users cannot have this overwritten.
                            </span>
                          </span>
                        ) : null}
                      </span>
                      <input
                        id={`parent-contact-${field.id}`}
                        className="student-input"
                        type={field.type}
                        autoComplete={field.autoComplete}
                        value={values[field.id]}
                        readOnly={locked}
                        onChange={(e) => {
                          setSaved("");
                          setValues((prev) => ({ ...prev, [field.id]: e.target.value }));
                        }}
                      />
                    </label>
                  );
                })
              )}
              {saveError ? (
                <div className="student-inline-error parent-field--wide" role="alert">
                  {saveError}
                </div>
              ) : null}
              {selected.editable && (dirty || saved) ? (
                <div className="parent-form-actions parent-field--wide">
                  {saved && !dirty ? <p className="parent-form-soon">Saved.</p> : null}
                  {dirty ? (
                    <button type="submit" className="student-btn" disabled={saving || loadingContact || !contact}>
                      {saving ? "Saving" : "Save"}
                    </button>
                  ) : null}
                </div>
              ) : !selected.editable ? (
                <p className="parent-form-soon parent-field--wide">View only. Updating this contact is a separate permission.</p>
              ) : null}
            </form>
          </section>
          {relationshipLinks.length ? (
            <section className="student-card parent-contact-panel" aria-labelledby="parent-relationships-heading">
              <h2 id="parent-relationships-heading" className="parent-contact-panel__title">Relationships</h2>
              <p className="parent-contact-panel__lede">
                {selected.kind === "student"
                  ? `How you are connected to ${selected.name}.`
                  : `How ${selected.kind === "self" ? "you are" : `${selected.name} is`} connected.`}
              </p>
              {relationshipLinks.map((link) => (
                <RelationshipEditor
                  key={link.relationshipId || `${link.studentUserId}:${link.userId}`}
                  link={link}
                  otherPrimary={guardians.some(
                    (row) =>
                      row.studentUserId === link.studentUserId &&
                      row.kind === "guardian" &&
                      row.relationshipId !== link.relationshipId &&
                      row.primary,
                  )}
                  onSaved={(next) =>
                    setGuardians((current) =>
                      current.map((row) => (row.relationshipId === next.relationshipId ? next : row)),
                    )
                  }
                />
              ))}
            </section>
          ) : null}
          {canManageGuardians && selected.kind === "student" ? (
            <section className="student-card parent-contact-panel" aria-labelledby="parent-add-guardian-heading">
              <AddGuardianForm
                studentUserId={selected.routeStudentId}
                hasPrimary={guardians.some((row) => row.studentUserId === selected.routeStudentId && row.kind === "guardian" && row.primary)}
                onAdded={(link) =>
                  setGuardians((current) => [
                    ...current.filter((row) => row.relationshipId !== link.relationshipId),
                    { ...link, studentUserId: selected.routeStudentId, studentName: selected.name },
                  ])
                }
              />
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
